import { useMemo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { compose } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { validationDisplayTypes } from '@gef-ui/form-material/constants';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider } from 'components';
import AutocompleteCustom from 'components/atoms/GefMui/AutocompleteCustom';
import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth';
import OutputTextField from 'components/atoms/GefMui/OutputTextField';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { KMP_NEW_CAMPAIGN } from 'features/common/acl/constants';
import { campaignTypeOptions } from 'features/views/campaignList/newCampaignModal/constants';
import { FIELDS } from 'features/views/campaignSettings/campaignDetail/form';
import { fromIsoDate, getCodelistPropById } from 'utils';

export const CampaignWave = ({ campaignChannels, campaignStates, campaignDetail }) => {
	const formData = useFormData();

	const campaignTypeValueForOutputTextField = useMemo(() => {
		const campaignType = campaignTypeOptions.find((item) => item.id === campaignDetail?.type);
		return campaignType?.description;
	}, [campaignDetail?.type]);

	const newOrEditCampaignPermission = useSelector((state) => hasPermissionByIds(state)(KMP_NEW_CAMPAIGN));

	return (
		<Paper sx={{ p: 3, borderRadius: 2 }}>
			<Stack spacing={2}>
				<SectionHeader text={getLabelMessage('campaign:campaignWaveTitle')} />
				<Grid container>
					<Grid size={12}>
						<Grid container spacing={2}>
							<Grid size={4}>
								{newOrEditCampaignPermission ? (
									<TextField
										label={getLabelMessage('campaign:campaignName')}
										size="small"
										fieldName={FIELDS.campaignName}
										validationDisplay={
											!formData?.campaignName ? validationDisplayTypes.HIDDEN : undefined
										}
										required
										slotProps={{
											htmlInput: {
												maxLength: MaxLengths.NAME,
											},
										}}
									/>
								) : (
									<OutputTextField
										size="small"
										id={FIELDS.campaignName}
										label={getLabelMessage('campaign:campaignName')}
										fullWidth
										value={campaignDetail.campaignName || ''}
										slotProps={{
											inputLabel: {
												shrink: !!campaignDetail.campaignName,
											},
										}}
									/>
								)}
							</Grid>
							<Grid size={2}>
								<OutputTextField
									size="small"
									id={FIELDS.status}
									label={getLabelMessage('campaign:status')}
									fullWidth
									value={getCodelistPropById(campaignDetail.campaignStateId, campaignStates) || ''}
									slotProps={{
										inputLabel: {
											shrink: !!campaignDetail.campaignStateId,
										},
									}}
								/>
							</Grid>
							<Grid size={2}>
								<OutputTextField
									size="small"
									id={FIELDS.campaignCode}
									label={getLabelMessage('campaign:campaignCode')}
									fullWidth
									value={campaignDetail.campaignCode || ''}
									slotProps={{
										inputLabel: {
											shrink: !!campaignDetail.campaignCode,
										},
									}}
								/>
							</Grid>
							<Grid size={2}>
								{newOrEditCampaignPermission ? (
									<DatePickerFullWidth
										label={getLabelMessage('campaign:startDate')}
										fieldName={FIELDS.startDate}
										validationDisplay={
											!formData?.startDate ? validationDisplayTypes.HIDDEN : undefined
										}
										required
									/>
								) : (
									<OutputTextField
										size="small"
										id={FIELDS.startDate}
										label={getLabelMessage('campaign:startDate')}
										fullWidth
										value={fromIsoDate(campaignDetail.startDate) || ''}
										slotProps={{
											inputLabel: {
												shrink: !!campaignDetail.startDate,
											},
										}}
									/>
								)}
							</Grid>
							<Grid size={2}>
								{newOrEditCampaignPermission ? (
									<DatePickerFullWidth
										label={getLabelMessage('campaign:endDate')}
										fieldName={FIELDS.endDate}
										validationDisplay={
											!formData?.endDate ? validationDisplayTypes.HIDDEN : undefined
										}
										required
									/>
								) : (
									<OutputTextField
										size="small"
										id={FIELDS.endDate}
										label={getLabelMessage('campaign:endDate')}
										fullWidth
										value={fromIsoDate(campaignDetail.endDate) || ''}
										slotProps={{
											inputLabel: {
												shrink: !!campaignDetail.endDate,
											},
										}}
									/>
								)}
							</Grid>
							<Grid size={3}>
								{newOrEditCampaignPermission ? (
									<TextField
										label={getLabelMessage('campaign:communicationName')}
										size="small"
										fieldName={FIELDS.communicationName}
										validationDisplay={
											!formData?.communicationName ? validationDisplayTypes.HIDDEN : undefined
										}
										required
										slotProps={{
											htmlInput: {
												maxLength: MaxLengths.NAME,
											},
										}}
									/>
								) : (
									<OutputTextField
										size="small"
										id={FIELDS.communicationName}
										label={getLabelMessage('campaign:communicationName')}
										fullWidth
										value={campaignDetail.communicationName || ''}
										slotProps={{
											inputLabel: {
												shrink: !!campaignDetail.communicationName,
											},
										}}
									/>
								)}
							</Grid>
							<Grid size={2}>
								{newOrEditCampaignPermission ? (
									<AutocompleteCustom
										size="small"
										fieldName={FIELDS.campaignType}
										label={getLabelMessage('campaign:campaignType')}
										options={campaignTypeOptions}
										labelPropName="description"
										valuePropName="id"
										validationDisplay={
											!formData?.campaignType ? validationDisplayTypes.HIDDEN : undefined
										}
										required
									/>
								) : (
									<OutputTextField
										size="small"
										id={FIELDS.campaignType}
										label={getLabelMessage('campaign:campaignType')}
										fullWidth
										value={campaignTypeValueForOutputTextField || ''}
										slotProps={{
											inputLabel: {
												shrink: !!campaignTypeValueForOutputTextField,
											},
										}}
									/>
								)}
							</Grid>
							<Grid size={3}>
								<OutputTextField
									size="small"
									id={FIELDS.communicationCode}
									label={getLabelMessage('campaign:communicationCode')}
									fullWidth
									value={campaignDetail.communicationCode || ''}
									slotProps={{
										inputLabel: {
											shrink: !!campaignDetail.communicationCode,
										},
									}}
								/>
							</Grid>
							<Grid size={2}>
								<OutputTextField
									size="small"
									id={FIELDS.contactCount}
									label={getLabelMessage('campaign:contactCount')}
									fullWidth
									value={isNaN(campaignDetail.contactCount) ? '' : campaignDetail.contactCount}
									slotProps={{
										inputLabel: {
											shrink: !isNilOrEmpty(campaignDetail.contactCount),
										},
									}}
								/>
							</Grid>
							<Grid size={2}>
								{newOrEditCampaignPermission ? (
									<AutocompleteCustom
										size="small"
										fieldName={FIELDS.channel}
										label={getLabelMessage('campaign:channel')}
										options={campaignChannels}
										validationDisplay={
											!formData?.channel ? validationDisplayTypes.HIDDEN : undefined
										}
										required
										labelPropName="description"
										valuePropName="id"
									/>
								) : (
									<OutputTextField
										size="small"
										id={FIELDS.channel}
										label={getLabelMessage('campaign:channel')}
										fullWidth
										value={getCodelistPropById(campaignDetail.channelId, campaignChannels) || ''}
										slotProps={{
											inputLabel: {
												shrink: !!campaignDetail.channelId,
											},
										}}
									/>
								)}
							</Grid>
							<Grid size={12} />
						</Grid>
					</Grid>
				</Grid>
			</Stack>
		</Paper>
	);
};

CampaignWave.propTypes = {
	campaignChannels: PropTypes.array,
	campaignDetail: PropTypes.object,
	campaignStates: PropTypes.array,
};

export default compose(
	codelistProvider(codelists.campaignChannels, 'campaignChannels'),
	codelistProvider(codelists.campaignStates, 'campaignStates'),
)(CampaignWave);
